/**
 * Organização e equipe (PLANO-EQUIPE-ORGANIZACAO.md, seções 3 e 6).
 *
 * Todo prestador tem uma organização por baixo; quem decide se ela é uma
 * equipe é o `type`. Solo não tem modos (`null`) e não vê nada de equipe.
 */

export type OrganizationType = 'SOLO' | 'TEAM';

export type MemberRole = 'OWNER' | 'MANAGER' | 'PROFESSIONAL';

/** `PAUSED_PLAN` = excedente depois de descer de plano (volta sozinho). */
export type MemberStatus = 'ACTIVE' | 'PAUSED_PLAN' | 'DISABLED' | 'REMOVED';

/** Quem recebe: dados de pagamento da organização ou do profissional. */
export type PaymentMode = 'ORGANIZATION' | 'PROFESSIONAL';

/** Por qual WhatsApp o cliente recebe mensagens. */
export type MessagingMode = 'ORGANIZATION' | 'PROFESSIONAL';

/** `SHARED`: todos veem os clientes da organização. `PRIVATE`: cada um os seus. */
export type ClientVisibility = 'SHARED' | 'PRIVATE';

export interface OrganizationModes {
  paymentMode: PaymentMode;
  messagingMode: MessagingMode;
  clientVisibility: ClientVisibility;
}

/** Os dois jeitos de trabalhar que a pergunta "Como sua equipe trabalha?" oferece. */
export type ModesPreset = 'SINGLE_TILL' | 'EACH_OWN';

export const MODES_PRESETS: Record<ModesPreset, OrganizationModes> = {
  SINGLE_TILL: {
    paymentMode: 'ORGANIZATION',
    messagingMode: 'ORGANIZATION',
    clientVisibility: 'SHARED',
  },
  EACH_OWN: {
    paymentMode: 'PROFESSIONAL',
    messagingMode: 'PROFESSIONAL',
    clientVisibility: 'PRIVATE',
  },
};

/** O vínculo do usuário logado, como vem em `GET /user/me`. */
export interface Membership {
  organizationId: string;
  organizationName: string;
  type: OrganizationType;
  role: MemberRole;
  status: MemberStatus;
  attends: boolean;
  /** `null` enquanto a organização for `SOLO`. */
  modes: OrganizationModes | null;
  /** Membros ativos, dono incluído. Decide se há com quem alternar. */
  activeMembers: number;
  /**
   * Virou organização e o plano de equipe ainda não foi confirmado (webhook ou
   * confirmação do checkout): o app só abre a tela de planos.
   */
  teamPlanPending?: boolean;
}

export interface Organization {
  id: string;
  name: string;
  type: OrganizationType;
  teamEnabledAt: string | null;
  paymentMode: PaymentMode | null;
  messagingMode: MessagingMode | null;
  clientVisibility: ClientVisibility | null;
  /** Plano de equipe contratado; `null` enquanto estiver num plano solo. */
  contractedProfessionals: number | null;
  contractedClients: number | null;
  billingSource: 'STRIPE' | 'APPLE' | null;
  /** O dono recebe também os avisos de agendamento de cada membro. */
  teamBookingAlerts: boolean;
  /** O meu vínculo com ela. */
  me: { role: MemberRole; attends: boolean };
}

export interface TeamMember {
  id: string;
  providerId: string;
  userId: string;
  name: string;
  image: string | null;
  role: MemberRole;
  status: MemberStatus;
  attends: boolean;
  color: string | null;
  displayOrder: number;
  /** WhatsApp que o dono informou ao criar. */
  invitePhone: string | null;
  inviteExpiresAt: string | null;
  /** `null` = perfil criado, o profissional ainda não entrou. */
  joinedAt: string | null;
  /** Só em desativado: agendamentos futuros que ainda estão na agenda dele. */
  pendingAppointments?: number;
}

/** Como o perfil novo começa: com os serviços e horários do dono ou vazio. */
export type MemberSetup = 'COPY_FROM_OWNER' | 'EMPTY';

export interface CreateMemberDto {
  name: string;
  phone: string;
  setup: MemberSetup;
}

export interface UpdateMemberDto {
  name?: string;
  color?: string | null;
  displayOrder?: number;
  attends?: boolean;
}

export interface BecomeTeamDto extends OrganizationModes {
  name: string;
}

export type UpdateOrganizationDto = Partial<OrganizationModes> & {
  name?: string;
  teamBookingAlerts?: boolean;
};

export interface InviteLink {
  url: string;
  /** Texto pronto para o WhatsApp, com o link dentro. */
  message: string;
  expiresAt: string;
}

/** Agendamento futuro devolvido ao desativar, para repassar ou cancelar. */
export interface DisabledMemberAppointment {
  id: string;
  startsAt: string;
  endsAt: string;
  clientName: string;
  serviceId: string;
  serviceName: string;
  /** Liga o serviço às cópias dos colegas: o repasse acha o serviço por ele. */
  catalogItemId: string | null;
}

export interface DisableMemberResult {
  member: TeamMember;
  futureAppointments: DisabledMemberAppointment[];
}

/**
 * Destino de um agendamento do membro desativado: passa para outro membro no
 * mesmo horário (com o serviço dele) ou é cancelado com aviso ao cliente.
 */
export type HandoverItem =
  | { appointmentId: string; action: 'TRANSFER'; toProviderId: string; serviceId: string }
  | { appointmentId: string; action: 'CANCEL' };

/** Por que um repasse não passou; o agendamento continua com o desativado. */
export type HandoverFailureCode = 'SLOT_TAKEN' | 'SERVICE_INVALID' | 'NOT_FOUND';

export interface HandoverResult {
  transferred: string[];
  cancelled: string[];
  failed: { appointmentId: string; code: HandoverFailureCode; message: string }[];
}

/** Números de um profissional no período (ou da equipe inteira, em `totals`). */
export interface TeamReportFigures {
  /** Recebido: cobranças pagas no período, líquidas de estorno. */
  revenue: number;
  /** A receber: cobranças pendentes/vencidas com vencimento no período. */
  pending: number;
  /** Agendamentos no período, cancelados fora. */
  appointments: number;
  completedAppointments: number;
  cancelledAppointments: number;
  /** Clientes distintos com agendamento no período (na equipe, sem repetir). */
  clientsServed: number;
}

export interface TeamReportMember extends TeamReportFigures {
  memberId: string;
  providerId: string;
  name: string;
  image: string | null;
  color: string | null;
  role: MemberRole;
  status: MemberStatus;
}

/** `GET /dashboard/team-report?from&to` — organização inteira, ignora o foco. */
export interface TeamReport {
  from: string;
  to: string;
  totals: TeamReportFigures;
  members: TeamReportMember[];
}

/** `GET /public/invite/:token`. */
export interface PublicInvite {
  organizationName: string;
  professionalName: string;
  /** "(11) •••••-4321". */
  maskedPhone: string;
  status: 'VALID' | 'EXPIRED' | 'USED';
}
